import { LeagueEntry } from '@api-service/mini-leagues';
import { getChartChipLongName } from '@components/live-rank-chart/utils';
import { FPL_SEASON_PLAYER_NUMBERS } from '@lib/constants';
import { resolveEntrySquad } from '@lib/page-utils/MiniLeagues/resolve-entry-squad';

export type MiniLeagueRowCaptain = {
  displayName: string;
  teamShortName: string;
  position: 'GK' | 'DEF' | 'MID' | 'FWD';
} | null;

export type MiniLeagueRow = {
  entry: LeagueEntry;
  leaguePosition: {
    /** Null until FPL publishes a rank, e.g. a brand-new entry or GW1. */
    leagueRank: number | null;
    positionChange: number | null;
  };
  /** Null until FPL publishes an overall rank. */
  liveRank: number | null;
  team: {
    playerName: string;
    teamName: string;
    entry: LeagueEntry;
    points: number;
  };
  total: number;
  points: number;
  hits: number;
  captain: MiniLeagueRowCaptain;
  viceCaptain: MiniLeagueRowCaptain;
  /**
   * Null when the league's source holds no transfer history for the entry, as
   * opposed to an empty list, which means the manager made no transfers.
   */
  transfers: LeagueEntry['transfers']['list'] | null;
  /** Null when the entry came without its squad. */
  played: number | null;
  teamId: number;
  chipInPlay: { chipName: string | null };
  remainingTransfers: number | null;
  userChips: { name: string; event: number }[];
};

const toCaptain = (
  player: LeagueEntry['captain'] | undefined
): MiniLeagueRowCaptain =>
  player
    ? {
        displayName: player.displayName,
        teamShortName: player.team.shortName,
        position: player.position
      }
    : null;

export const miniLeagueTransformer = (
  entries: LeagueEntry[] | undefined
): MiniLeagueRow[] => {
  if (!entries?.length) return [];

  return entries.map((entry) => ({
    entry,
    leaguePosition: {
      // The live estimate, deliberately in preference to the settled
      // `officialLeagueRank`: while matches are on, a member expects the table
      // to move with the points they can see. The two agree outside a live
      // window, so this only differs mid-gameweek.
      leagueRank: entry.rank.league,
      positionChange: entry.rank.change
    },
    liveRank: entry.rank.overall,
    team: {
      playerName: entry.playerName,
      teamName: entry.teamName,
      entry,
      points: entry.points.gameweek
    },
    total: entry.points.overall,
    points: entry.points.gameweek,
    hits: entry.transfers.hits,
    // Carried on the entry apart from its squad, so a table sent without
    // squads can still show the armband.
    captain: toCaptain(entry.captain),
    viceCaptain: toCaptain(entry.viceCaptain),
    // An unknown count means the source holds no transfer history, so the
    // empty list is not a statement that the manager made none.
    transfers: entry.transfers.count === null ? null : entry.transfers.list,
    remainingTransfers: entry.transfers.remaining,
    played:
      entry.starting.length === 0 ? null : resolveEntrySquad(entry).playedCount,
    teamId: entry.teamId,
    chipInPlay: { chipName: entry.activeChip },
    userChips: entry.usedChips.map((chip) => ({
      name: getChartChipLongName(chip.name) ?? chip.name,
      event: chip.gameweek
    }))
  }));
};

export const seasonHistoryTableTransformer = (data) => {
  if (!data) return [];
  return data?.map((item) => ({
    seasonName: item.season_name,
    totalPoints: item.total_points,
    rank: item.rank,
    percentage: (item.rank / FPL_SEASON_PLAYER_NUMBERS[item.season_name]) * 100
  }));
};
